import { countries } from 'countries-list';
import { DropdownOption } from "@hawkins/components";
import { DropdownFormControl, DropdownFormControlProps, FieldValues, useFormContext } from "@hawkins/forms";
import { useTranslation } from "react-i18next";
import { useEffect } from 'react';

import { TranslationNamespace } from '../../types';

const SUPPORTED_COUNTRIES = [
  'AX',
  'AL',
  'AD',
  'AI',
  'AG',
  'AR',
  'AW',
  'AT',
  'BS',
  'BB',
  'BE',
  'BZ',
  'BM',
  'BO',
  'BQ',
  'BA',
  'BV',
  'BR',
  'IO',
  'BG',
  'KY',
  'CL',
  'CO',
  'CR',
  'HR',
  'CU',
  'CW',
  'CY',
  'CZ',
  'DK',
  'DM',
  'DO',
  'EC',
  'SV',
  'EE',
  'FK',
  'FO',
  'FI',
  'FR',
  'GF',
  'TF',
  'DE',
  'GI',
  'GR',
  'GL',
  'GD',
  'GP',
  'GT',
  'GG',
  'GY',
  'HT',
  'VA',
  'HN',
  'HU',
  'IS',
  'IE',
  'IM',
  'IL',
  'IT',
  'JM',
  'JE',
  'XK',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'MQ',
  'YT',
  'EA',
  'MC',
  'ME',
  'MS',
  'NL',
  'AN',
  'NI',
  'NO',
  'PA',
  'PY',
  'PE',
  'PL',
  'PT',
  'PR',
  'RE',
  'RO',
  'BL',
  'SH',
  'KN',
  'LC',
  'MF',
  'PM',
  'VC',
  'SM',
  'RS',
  'SX',
  'SK',
  'SI',
  'GS',
  'ES',
  'SR',
  'SJ',
  'SE',
  'CH',
  'TT',
  'TC',
  'GB',
  'US',
  'UY',
  'VE',
  'VG',
  'VI',
];

export function CountryDropDown<T extends FieldValues>({ ...args }: DropdownFormControlProps<DropdownOption<string>, T>) {
  const { t, i18n } = useTranslation(TranslationNamespace.LEP);
  const formContext = useFormContext();

  // need to manually add countries that aren't included in countries-list pckg
  const moreCountries = {
    AN: {
      name: 'Netherlands Antilles',
      native: 'Nederlandse Antillen',
      phone: '599',
      continent: 'SA',
      capital: 'Willemstad',
      currency: 'ANG',
      languages: ['nl', 'en'],
      emoji: '🇧🇶',
      emojiU: 'U+1F1E7 U+1F1F6',
    },
    EA: {
      name: 'Melilla',
      native: 'Melilla',
      phone: '952',
      continent: 'AF',
      capital: '',
      currency: 'EUR',
      languages: ['es'],
      emoji: '🇪🇦',
      emojiU: 'U+1F1EA U+1F1E6',
    },
  };

  const countryOptions: DropdownOption<string>[] = Object.entries({...countries, ...moreCountries})
    .filter(([code]) => SUPPORTED_COUNTRIES.includes(code))
    .map(([code]) => ({
      label: t(`country_${code.toLowerCase()}`),
      value: code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Update the selected country's label when language changes
  useEffect(() => {
    if (formContext && args.name) {
      const currentValue = formContext.getValues(args.name);
      if (currentValue && typeof currentValue === 'object' && 'value' in currentValue && currentValue.value) {
        const countryCode = currentValue.value;
        const translatedLabel = t(`country_${countryCode.toLowerCase()}`);
        
        // Only update if the label has changed
        if (currentValue.label !== translatedLabel) {
          formContext.setValue(args.name, {
            value: countryCode,
            label: translatedLabel
          } as any, { shouldDirty: false });
        }
      }
    }
  }, [i18n.language, name, formContext]); 
  
  return (
    <DropdownFormControl<DropdownOption<string>, T>
      options={countryOptions}
      {...args}
      searchable
      sortOptions
      data-testid='country-dropdown'
    />
  );
};